/**
 * CampaignsPage Component
 *
 * Main campaigns page that combines the sidebar with the chat interface.
 * Provides full campaign management experience.
 *
 * Usage:
 *   <Route path="/campaigns" element={<CampaignsPage />} />
 *   <Route path="/campaigns/:campaignId" element={<CampaignsPage />} />
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Megaphone } from 'lucide-react';
import { CampaignSidebar, CampaignSidebarRef } from '@/components/campaigns/CampaignSidebar';
import { ChatInterface } from '@/components/chat/ChatInterface';
import { useMobile } from '@/contexts/MobileContext';
import { apiClient } from '@/services/api';
import { useToast } from '@/components/ui/Toast';
import { CapabilityCard, CAPABILITY } from '@/components/onboarding';
import { CampaignsListPage } from './CampaignsListPage';
import { brandKnowledgeApi } from '@/services/brandKnowledgeApi';

export const CampaignsPage: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const { isSidebarOpen, closeSidebar } = useMobile();
  const { toast } = useToast();
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | undefined>(campaignId);
  const sidebarRef = useRef<CampaignSidebarRef>(null);
  const isOrgSwitchingRef = useRef(false);

  // Sync selected campaign with URL param (but not if we're switching orgs)
  useEffect(() => {
    if (!isOrgSwitchingRef.current) {
      setSelectedCampaignId(campaignId);
    }
  }, [campaignId]);

  // Refresh campaigns when organization changes
  useEffect(() => {
    const handleOrgChange = () => {
      // Mark that we're switching to prevent URL param sync
      isOrgSwitchingRef.current = true;

      // Immediately clear URL and selection
      setSelectedCampaignId(undefined);
      navigate('/campaigns', { replace: true });

      // Refresh sidebar in background (will silently load new org's campaigns)
      sidebarRef.current?.refreshCampaigns();

      // Reset the flag after navigation completes
      setTimeout(() => {
        isOrgSwitchingRef.current = false;
      }, 100);
    };

    window.addEventListener('organizationChanged', handleOrgChange);
    return () => window.removeEventListener('organizationChanged', handleOrgChange);
  }, [navigate]);

  // Handle conversation not found (404/403) - e.g., conversation from previous org
  useEffect(() => {
    const handleConversationNotFound = () => {
      // Clear selection and go to campaigns
      setSelectedCampaignId(undefined);
      navigate('/campaigns', { replace: true });
    };

    window.addEventListener('conversationNotFound', handleConversationNotFound);
    return () => window.removeEventListener('conversationNotFound', handleConversationNotFound);
  }, [navigate]);

  const handleSelectCampaign = (id: string) => {
    navigate(`/campaigns/${id}`);
  };

  const handleNewCampaign = async () => {
    try {
      // Create conversation immediately
      const response = await apiClient.post('/api/v1/campaigns', { title: 'New Campaign' });
      const newId = response.data.id;

      // Navigate to the new conversation
      navigate(`/campaigns/${newId}`);
      setSelectedCampaignId(newId);

      // Refresh sidebar
      sidebarRef.current?.refreshCampaigns();
    } catch (err: any) {
      console.error('Failed to create campaign:', err);
      const status = err.response?.status;
      const errorData = err.response?.data?.error || err.response?.data;
      if (status === 402) {
        const msg = errorData?.limit != null
          ? `Campaign limit reached (${errorData.current}/${errorData.limit}). Upgrade your plan to create more campaigns.`
          : errorData?.message || 'Campaign creation requires a plan upgrade.';
        toast.warning(msg, 'Limit reached');
      } else {
        // Fallback to old behavior for non-limit errors
        navigate('/campaigns/new');
        setSelectedCampaignId(undefined);
      }
    }
  };

  const handleConversationCreated = () => {
    // Refresh sidebar when new conversation is created
    sidebarRef.current?.refreshCampaigns();
  };

  // A month's posts container holds the plan's posts for the scheduler; its campaign page shows
  // none of them, so it opens as that month's Posts page.
  useEffect(() => {
    if (!campaignId) return;
    let off = false;
    brandKnowledgeApi.postsContainers()
      .then(list => { const hit = list.find(x => x.conversation_id === campaignId); if (hit && !off) navigate(`/month/${hit.month}/posts`, { replace: true }); })
      .catch(() => { /* not a container, or brand knowledge unavailable: the campaign page stays */ });
    return () => { off = true; };
  }, [campaignId, navigate]);

  // A campaign is a page of its own (campaign page board); the list lives on /campaigns.
  if (campaignId) {
    return (
      <div className="flex h-full flex-1 overflow-hidden">
        <main className="flex-1 overflow-hidden">
          <ChatInterface key={campaignId} onConversationCreated={handleConversationCreated} />
        </main>
      </div>
    );
  }

  // /campaigns is the campaigns list (board D2).
  return <CampaignsListPage />;
};

// Welcome screen when no campaign is selected
const WelcomeScreen: React.FC<{ onNewCampaign: () => void }> = ({ onNewCampaign }) => {
  const chips = [
    'Festive sale for my jewellery store, ₹5,000 for 7 days',
    'Launch a new face serum to first-time buyers',
    'Retarget people who viewed my watch page',
  ];

  return (
    <div className="h-full flex items-center justify-center bg-background-light dark:bg-background-dark overflow-y-auto">
      <div className="max-w-2xl mx-auto px-8 py-12">
        <CapabilityCard
          icon={Megaphone}
          tone="navy"
          title={CAPABILITY.campaigns_empty.title}
          body={CAPABILITY.campaigns_empty.body}
          steps={[
            { label: 'STEP 1', title: 'Describe the product', body: 'Or point us at an existing creative.' },
            { label: 'STEP 2', title: 'Review 5 variations', body: 'Swap copy or images. Keep what you like.' },
            { label: 'STEP 3', title: 'Publish or schedule', body: 'One button. Budget split handled.' },
          ]}
          primary={{ label: CAPABILITY.campaigns_empty.primary.label, onClick: onNewCampaign }}
          chips={chips.map((label) => ({
            label: `"${label}"`,
            onClick: () => {
              onNewCampaign();
              // Store the chip text so ChatInterface can pick it up on mount
              sessionStorage.setItem('admeru_prefill_input', label);
            },
          }))}
        />
      </div>
    </div>
  );
};

export default CampaignsPage;
